// ============================================
// Unified CanvasEntity + kind-specific data types
// ============================================

export const NODE_WIDTH = 200;
export const NODE_HEIGHT = 60;

export const DEFAULT_TEXTBLOCK_WIDTH = 200;
export const DEFAULT_TEXTBLOCK_HEIGHT = 80;
export const MIN_TEXTBLOCK_WIDTH = 60;
export const MIN_TEXTBLOCK_HEIGHT = 30;

export const DEFAULT_BLOCKNODE_WIDTH = 400;
export const DEFAULT_BLOCKNODE_HEIGHT = 250;
export const MIN_BLOCKNODE_WIDTH = 200;
export const MIN_BLOCKNODE_HEIGHT = 120;

export const DEFAULT_SPRITE_SIZE = 64;
export const MIN_SPRITE_SIZE = 24;

export const DEFAULT_IMAGE_WIDTH = 300;
export const MIN_IMAGE_SIZE = 40;

export const DEFAULT_SHAPE_WIDTH = 160;
export const DEFAULT_SHAPE_HEIGHT = 120;
export const MIN_SHAPE_WIDTH = 40;
export const MIN_SHAPE_HEIGHT = 40;

export const COLLAPSED_GROUP_WIDTH = 160;
export const COLLAPSED_GROUP_HEIGHT = 40;
export const GROUP_PADDING = 40;

export type ConnectorSide = 'top' | 'right' | 'bottom' | 'left';
export const ALL_CONNECTOR_SIDES: ConnectorSide[] = ['top', 'right', 'bottom', 'left'];

export type EntityKind = 'task' | 'textblock' | 'textlabel' | 'group' | 'waypoint' | 'connector' | 'sprite' | 'image' | 'blocknode' | 'shape' | 'adr-trigger';

export type SegmentType = 'bezier' | 'straight' | 'ortho90' | 'angle45' | 'elbowed';
export type StrokeStyle = 'solid' | 'dashed' | 'dotted' | 'dashdot' | 'longdash' | 'shortdash' | 'longdashdotdot';
export type ArrowMarkerType = 'none' | 'arrow' | 'open-arrow' | 'triangle' | 'diamond' | 'circle' | 'bar' | 'square';

// ============================================
// ConnectorEndpoint (unified, replaces SegmentEndpoint)
// ============================================

export interface ConnectorEndpoint {
  entityId: string;
  side: ConnectorSide | 'center'; // 'center' for waypoints
}

// ============================================
// Kind-specific data shapes
// ============================================

export interface TaskData {
  taskId: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderWidth?: number;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
}

export interface TextBlockData {
  text: string;
  html?: string;
  backgroundColor: string;
  textColor: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  textAlign: 'left' | 'center' | 'right';
  borderColor?: string;
  borderWidth?: number;
}

export interface TextLabelData {
  text: string;
  html?: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  textColor: string;
  borderColor?: string;
  borderWidth?: number;
}

export interface GroupData {
  label: string;
  description?: string;
  backgroundColor: string;
  collapsed: boolean;
  collapsedX?: number;
  collapsedY?: number;
  memberEntityIds: string[];
  displayMode?: 'invisible' | 'container';
  borderColor?: string;
  borderWidth?: number;
}

export interface WaypointData {}

export interface ConnectorData {
  from: ConnectorEndpoint;
  to: ConnectorEndpoint;
  segmentType: SegmentType;
  // Styling (all optional → defaults in rendering)
  strokeColor?: string;
  strokeWidth?: number;
  strokeStyle?: StrokeStyle;
  arrowStart?: ArrowMarkerType;
  arrowEnd?: ArrowMarkerType;
  label?: string;
  labelOffsetX?: number;
  labelOffsetY?: number;
}

export const DEFAULT_EDGE_COLOR = '#9ca3af';
export const DEFAULT_EDGE_WIDTH = 2;
export const DEFAULT_STROKE_STYLE: StrokeStyle = 'solid';
export const DEFAULT_ARROW_START: ArrowMarkerType = 'none';

// ============================================
// CanvasEntity (unified)
// ============================================

export interface CanvasEntity {
  id: string;
  kind: EntityKind;
  x: number;
  y: number;
  width: number;
  height: number;
  layerId: string;       // '' for waypoint/connector
  connectable: boolean;  // has connector ports?
  zIndex: number;        // rendering order (higher = on top), auto-assigned by entity.add/batchAdd if missing
  data: Record<string, unknown>;
}

// ============================================
// Rect type for bounds
// ============================================

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// ============================================
// ConnectorPosition for connector port locations
// ============================================

export interface ConnectorPosition {
  side: ConnectorSide | 'center';
  x: number;
  y: number;
}

// ============================================
// Helper: typed data accessors
// ============================================

export function getTaskData(entity: CanvasEntity): TaskData {
  return entity.data as unknown as TaskData;
}

export function getTextBlockData(entity: CanvasEntity): TextBlockData {
  return entity.data as unknown as TextBlockData;
}

export function getTextLabelData(entity: CanvasEntity): TextLabelData {
  return entity.data as unknown as TextLabelData;
}

export function getGroupData(entity: CanvasEntity): GroupData {
  return entity.data as unknown as GroupData;
}

export function getConnectorData(entity: CanvasEntity): ConnectorData {
  return entity.data as unknown as ConnectorData;
}

export function getSpriteData(entity: CanvasEntity): import('./sprites/spriteTypes').SpriteData {
  return entity.data as unknown as import('./sprites/spriteTypes').SpriteData;
}

export interface ImageData {
  src: string;            // base64 data URL
  naturalWidth: number;   // Originalbreite
  naturalHeight: number;  // Originalhoehe
  borderRadius: number;   // 0 | 4 | 8 | 16
  opacity: number;        // 0.2 - 1.0
  borderColor?: string;
  borderWidth?: number;
}

export function getImageData(entity: CanvasEntity): ImageData {
  return entity.data as unknown as ImageData;
}

export interface BlockNodeData {
  content: unknown[];       // BlockNote Block[] stored as unknown[] to avoid BN type import
  backgroundColor: string;
  borderColor?: string;
  borderWidth?: number;
}

export function getBlockNodeData(entity: CanvasEntity): BlockNodeData {
  return entity.data as unknown as BlockNodeData;
}

export interface ShapeData {
  shapeType: 'rect' | 'ellipse' | 'diamond' | 'triangle';
  backgroundColor: string;
  borderColor?: string;
  borderWidth?: number;
  cornerRadius?: number;   // nur fuer rect, default 0
}

export function getShapeData(entity: CanvasEntity): ShapeData {
  return entity.data as unknown as ShapeData;
}

export interface AdrTriggerData {
  adrIds: string[];
  color?: string; // hex, default '#ef4444'
}

export function getAdrTriggerData(entity: CanvasEntity): AdrTriggerData {
  return entity.data as unknown as AdrTriggerData;
}

export const ADR_TRIGGER_SIZE = 24;

// ============================================
// Attached Resources (any entity can have resources)
// ============================================

export function getAttachedResourceIds(entity: CanvasEntity): string[] {
  return ((entity.data as any).attachedResourceIds as string[] | undefined) ?? [];
}

// ============================================
// Helper: generate entity IDs
// ============================================

export function generateEntityId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// ============================================
// Helper: connector point calculation (same as old types.ts)
// ============================================

export function getConnectorPointForRect(
  x: number,
  y: number,
  width: number,
  height: number,
  side: ConnectorSide,
): { x: number; y: number } {
  switch (side) {
    case 'top':    return { x: x + width / 2, y: y };
    case 'right':  return { x: x + width, y: y + height / 2 };
    case 'bottom': return { x: x + width / 2, y: y + height };
    case 'left':   return { x: x, y: y + height / 2 };
  }
}
